import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from 'dreambel-ui';
import { downloadFramerie } from '../utils/download';
import { scrollToSection } from '../utils/scroll';
import { SectionIntro } from '@/landing/components/SectionIntro';
import { useTranslation } from '@/i18n/hooks/useTranslation';

export function Hero() {
  const heroRef = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  // Suivre le scroll de la section Hero
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  // Transformer le scroll en valeurs de scale et width
  const scale = useTransform(scrollYProgress, [0, 1], [0.8, 1.1]);
  const width = useTransform(scrollYProgress, [0, 1], ['60%', '75%']);

  const heroTitle = (
    <>
      {t('hero.title.part1')} <i>{t('hero.title.art')}</i>
      <br /> {t('hero.title.part2')} <b>Framerie</b>
    </>
  );

  return (
    <section id="hero" ref={heroRef} className="section hero">
      <SectionIntro
        className="w-[75%]"
        title={heroTitle}
        subtitle={t('hero.subtitle')}
      >
        <div className="flex flex-col sm:flex-row gap-4 mt-8 w-full sm:w-[50%] h-[8rem] sm:h-auto">
          <Button
            className="h-[4rem] flex-1 flex justify-center text-lg text-md-on-primary hover:border-md-outline"
            variant="outlined"
            onClick={downloadFramerie}
          >
            <svg
              className="size-6 text-gray-12 opacity-90"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 256 256"
            >
              <path d="M112 144v51.64a8 8 0 0 1-8 8 8.5 8.5 0 0 1-1.43-.13l-64-11.64A8 8 0 0 1 32 184v-40a8 8 0 0 1 8-8h64a8 8 0 0 1 8 8m-2.87-89.78a8 8 0 0 0-6.56-1.73l-64 11.64A8 8 0 0 0 32 72v40a8 8 0 0 0 8 8h64a8 8 0 0 0 8-8V60.36a8 8 0 0 0-2.87-6.14M216 136h-80a8 8 0 0 0-8 8v57.45a8 8 0 0 0 6.57 7.88l80 14.54a7.6 7.6 0 0 0 1.43.13 8 8 0 0 0 8-8v-72a8 8 0 0 0-8-8m5.13-102.14a8 8 0 0 0-6.56-1.73l-80 14.55a8 8 0 0 0-6.57 7.87V112a8 8 0 0 0 8 8h80a8 8 0 0 0 8-8V40a8 8 0 0 0-2.87-6.14"></path>
            </svg>
            {t('common.actions.downloadForFree')}
          </Button>
          <Button
            className="h-[4rem] flex-1 flex justify-center text-lg"
            variant="filled"
            onClick={() => scrollToSection('pricing')}
          >
            {t('common.actions.updatePremium')}
          </Button>
        </div>
      </SectionIntro>

      {/* Video Demo Frame */}
      <motion.div
        className="frame"
        style={{
          scale,
          width,
          maxWidth: '75vw',
          margin: '0 auto',
        }}
        transition={{
          type: 'spring',
          stiffness: 100,
          damping: 30,
          mass: 1,
        }}
      >
        <div className="frame_inner">
          <video
            className="hero_video"
            preload="auto"
            aria-label={t('hero.video.ariaLabel')}
            muted
            loop
            autoPlay
            playsInline
          >
            <source src="/assets/framerie-demo.mp4" type="video/mp4" />
            <p className="text-center p-4">{t('hero.video.fallback')}</p>
          </video>
          <div className="video-overlay" />
        </div>
      </motion.div>
    </section>
  );
}
